<!-- Hidden file input -->
<input #fileInput type="file" class="hidden" accept=".pdf,application/pdf" (change)="onFileSelected($event)" />

<div class="max-w-2xl mx-auto space-y-5">

  <!-- Existing uploads -->
  @if (stepper.documentsLoading()) {
    <app-loading-spinner size="sm" [framed]="true" [title]="'PROJECT_STEPPER.STEP2.CHECKING_TITLE' | translate" [message]="'PROJECT_STEPPER.STEP2.CHECKING_MESSAGE' | translate" />
  } @else if (stepper.existingDocuments().length > 0) {
    <div class="rounded overflow-hidden bg-[var(--talos-bg)]" style="border:1px solid rgb(var(--talos-accent-rgb) / 0.16)">
      <div class="px-5 py-3 flex items-center gap-2.5" style="border-bottom:1px solid rgb(var(--talos-accent-rgb) / 0.14)">
        <div class="w-5 h-5 rounded-full flex items-center justify-center bg-[rgba(22,200,80,0.12)] shrink-0">
          <lucide-icon [img]="CheckCircleIcon" [size]="12" color="#16a34a" />
        </div>
        <span class="text-sm font-semibold text-talos-ink">{{ 'PROJECT_STEPPER.STEP2.UPLOADED_DOCUMENTS' | translate }}</span>
        <span class="ml-auto text-[10px] font-bold uppercase tracking-[0.1em]" style="color:rgb(var(--talos-ink-rgb) / 0.38)">
          {{ (stepper.existingDocuments().length === 1 ? 'PROJECT_STEPPER.STEP2.FILE_COUNT_ONE' : 'PROJECT_STEPPER.STEP2.FILE_COUNT_MANY') | translate: { count: stepper.existingDocuments().length } }}
        </span>
      </div>
      <ul>
        @for (doc of stepper.existingDocuments(); track doc.doc_id) {
          <li class="flex items-center gap-3 px-5 py-3.5" style="border-bottom:1px solid rgb(var(--talos-accent-rgb) / 0.1)">
            <div class="w-8 h-8 rounded shrink-0 flex items-center justify-center" style="background:rgb(var(--talos-ink-rgb) / 0.05)">
              <lucide-icon [img]="FileIcon" [size]="15" color="rgb(var(--talos-ink-rgb) / 0.4)" />
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-talos-ink truncate">{{ filenameFromUrl(doc.document_url) }}</p>
              <p class="text-[11px] mt-0.5" style="color:rgb(var(--talos-ink-rgb) / 0.4)">{{ 'PROJECT_STEPPER.STEP2.UPLOADED_AT' | translate: { date: doc.date_time_updated } }}</p>
            </div>
            <div class="flex items-center gap-1 shrink-0">
              <a [href]="doc.document_url" target="_blank" rel="noopener"
                class="w-7 h-7 rounded flex items-center justify-center transition-colors hover:bg-talos-ink/6"
                style="color:rgb(var(--talos-ink-rgb) / 0.4)">
                <lucide-icon [img]="ExternalLinkIcon" [size]="13" />
              </a>
              <app-button variant="danger-ghost" [iconOnly]="true" size="sm" [icon]="TrashIcon" [tooltip]="'PROJECT_STEPPER.STEP2.DELETE_DOCUMENT' | translate"
                [loading]="deleting() === doc.doc_id" [disabled]="deleting() !== null" (clicked)="deleteDoc(doc.doc_id)" />
            </div>
          </li>
        }
      </ul>
    </div>
  }

  <!-- Drop zone -->
  @if (!selectedFile()) {
    <div
      class="rounded transition-all duration-200 cursor-pointer"
      [style.border]="dragOver() ? '2px dashed var(--talos-accent)' : '2px dashed rgb(var(--talos-accent-rgb) / 0.22)'"
      [style.background]="dragOver() ? 'rgb(var(--talos-accent-rgb) / 0.03)' : 'var(--talos-bg)'"
      role="button"
      tabindex="0"
      [attr.aria-label]="'PROJECT_STEPPER.STEP2.BROWSE_FILES' | translate"
      (dragover)="onDragOver($event)"
      (dragleave)="onDragLeave()"
      (drop)="onDrop($event)"
      (click)="openFileBrowser()"
      (keydown.enter)="openFileBrowser()"
      (keydown.space)="$event.preventDefault(); openFileBrowser()"
    >
      <div class="flex flex-col items-center justify-center px-8 py-14 text-center">
        <!-- Icon -->
        <div class="w-14 h-14 rounded flex items-center justify-center mb-5 transition-colors"
          [style.background]="dragOver() ? 'rgb(var(--talos-accent-rgb) / 0.08)' : 'rgb(var(--talos-ink-rgb) / 0.06)'">
          <lucide-icon [img]="UploadCloudIcon" [size]="26" [color]="dragOver() ? 'var(--talos-accent)' : 'rgb(var(--talos-ink-rgb) / 0.3)'" />
        </div>

        <p class="text-base font-bold text-talos-ink mb-1.5">
          {{ (stepper.existingDocuments().length > 0 ? 'PROJECT_STEPPER.STEP2.UPLOAD_NEW_VERSION' : 'PROJECT_STEPPER.STEP2.DROPZONE_TITLE') | translate }}
        </p>
        <p class="text-sm mb-6" style="color:rgb(var(--talos-ink-rgb) / 0.4)">{{ 'PROJECT_STEPPER.STEP2.BROWSE_HINT' | translate }}</p>

        <div (click)="$event.stopPropagation()">
          <app-button [label]="'PROJECT_STEPPER.STEP2.BROWSE_FILES' | translate" variant="primary" [icon]="FileIcon" (clicked)="openFileBrowser()" />
        </div>

        <div class="mt-6 inline-flex items-center gap-2 rounded px-4 py-2 text-xs"
          style="background:rgb(var(--talos-ink-rgb) / 0.04);color:rgb(var(--talos-ink-rgb) / 0.45)">
          <span class="w-1 h-1 rounded-full bg-talos-ink/30 inline-block"></span>
          <span>{{ 'PROJECT_STEPPER.STEP2.PDF_ONLY' | translate }}</span>
        </div>

        @if (error()) {
          <p class="mt-4 text-xs font-medium text-talos-accent">{{ error() }}</p>
        }
      </div>
    </div>

  } @else {

    <!-- File selected / uploading card -->
    <div class="rounded bg-[var(--talos-bg)] p-6" style="border:1px solid rgb(var(--talos-accent-rgb) / 0.16)">

      <!-- File row -->
      <div class="flex items-center gap-4">
        <div class="w-11 h-11 rounded shrink-0 flex items-center justify-center" style="background:rgb(var(--talos-ink-rgb) / 0.05)">
          <lucide-icon [img]="FileIcon" [size]="20" color="rgb(var(--talos-ink-rgb) / 0.45)" />
        </div>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-semibold text-talos-ink truncate">{{ selectedFile()!.name }}</p>
          <p class="text-xs mt-0.5" style="color:rgb(var(--talos-ink-rgb) / 0.4)">{{ formatSize(selectedFile()!.size) }}</p>
        </div>
        @if (!uploading()) {
          <button type="button"
            class="w-8 h-8 rounded flex items-center justify-center border-0 cursor-pointer transition-colors hover:bg-talos-ink/6"
            style="background:rgb(var(--talos-ink-rgb) / 0.04)"
            (click)="removeFile()">
            <lucide-icon [img]="XIcon" [size]="14" color="rgb(var(--talos-ink-rgb) / 0.45)" />
          </button>
        }
      </div>

      <!-- Progress / status -->
      <div class="mt-5">
        @if (uploading()) {
          <div class="flex justify-between text-xs mb-2" style="color:rgb(var(--talos-ink-rgb) / 0.4)">
            <span>{{ 'PROJECT_STEPPER.STEP2.UPLOADING' | translate }}</span>
            <span class="tabular-nums font-semibold text-talos-accent">{{ uploadProgress() }}%</span>
          </div>
          <div class="w-full h-1 rounded-full overflow-hidden" style="background:rgb(var(--talos-ink-rgb) / 0.08)">
            <div class="h-full rounded-full bg-talos-ink transition-[width] duration-150 ease-out" [style.width.%]="uploadProgress()"></div>
          </div>
        } @else if (stepper.uploadComplete()) {
          <div class="flex items-center gap-2 text-xs font-semibold text-green-600">
            <lucide-icon [img]="CheckCircleIcon" [size]="14" color="#16a34a" />
            {{ 'PROJECT_STEPPER.STEP2.UPLOADED_SUCCESS' | translate }}
          </div>
        } @else if (error()) {
          <p class="text-xs text-talos-accent">{{ error() }}</p>
        }
      </div>

      <!-- Meta strip -->
      <div class="mt-5 flex items-center gap-2 rounded px-4 py-2 text-xs"
        style="background:rgb(var(--talos-ink-rgb) / 0.04);color:rgb(var(--talos-ink-rgb) / 0.45)">
        <span class="w-1 h-1 rounded-full bg-talos-ink/30 inline-block"></span>
        <span>{{ 'PROJECT_STEPPER.STEP2.PDF_ONLY' | translate }}</span>
      </div>

      @if (!uploading()) {
        <button type="button"
          class="mt-4 text-xs font-semibold text-talos-accent hover:underline bg-transparent border-0 cursor-pointer p-0"
          (click)="openFileBrowser()">
          {{ 'PROJECT_STEPPER.STEP2.CHANGE_FILE' | translate }}
        </button>
      }

    </div>
  }

</div>
